import React, { useRef, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  KeyRound,
  ShieldCheck,
  Sparkles,
  Plane,
  ExternalLink,
  Volume2,
  ListChecks,
  CheckCircle2,
  AlertTriangle,
  Download,
  Workflow,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";

// Onboarding video — direct MP4 streamed from the customer-assets CDN.
//   - 167 MB so we deliberately do NOT bundle it; the CDN supports HTTP
//     range requests so the browser only fetches what it needs to play.
//   - `preload="metadata"` keeps first-paint cheap until the user clicks.
//   - The Cloudflare Stream iframe/SDK/fallback machinery used previously
//     is no longer needed — with a same-origin-CORS-allowed <video> we can
//     call .play() + set .muted = false directly inside the user-click
//     handler, which is the most reliable way to get unmuted playback
//     past every browser's autoplay-block heuristic.
const VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_affiliate-ai-6/artifacts/ozv2xxgl_smalller.mp4";

export const ApiKeyGate = ({ onSave }) => {
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  // `started` flips to true the first time the user clicks "Watch with
  // sound" — used solely to hide the overlay button. The <video> element
  // itself is not remounted; we drive playback in-place.
  const [started, setStarted] = useState(false);
  const videoRef = useRef(null);

  const handleWatchWithSound = () => {
    const v = videoRef.current;
    if (!v) return;
    // Order matters: unmute → restart → play, all synchronously inside the
    // click handler so the browser registers it as a user-initiated play
    // and lets sound through.
    try {
      v.muted = false;
      v.volume = 1;
      try { v.currentTime = 0; } catch (_) { /* metadata may not be loaded yet */ }
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Browser still blocked unmuted play — fall back to the native
          // controls (already rendered) so the user can tap play manually.
          toast.error("Tap the video's play button to start with sound.");
        });
      }
      setStarted(true);
    } catch (_) {
      toast.error("Couldn't start the video — please use the player controls.");
    }
  };

  const save = () => {
    const trimmed = value.trim();
    if (!trimmed.startsWith("sk-")) {
      toast.error("That doesn't look like an OpenAI key (should start with sk-)");
      return;
    }
    localStorage.setItem("buddypass_openai_key", trimmed);
    toast.success("Key saved. Welcome aboard.");
    onSave(trimmed);
  };

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-zinc-900/60 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-zinc-400">
            <Plane className="h-3.5 w-3.5 text-amber-400 -rotate-45" strokeWidth={1.8} />
            Powered by The Nomadic Wealth Academy
          </div>
          <h1 className="font-display mt-5 text-4xl font-black tracking-tighter text-white sm:text-5xl">
            BuddyPass <span className="text-amber-400">AI</span>™
          </h1>
          <p className="mx-auto mt-4 max-w-md text-sm text-zinc-400 leading-relaxed">
            Create, build, earn — from anywhere, using AI.
          </p>
        </div>

        {/* Onboarding video — native <video> streaming the MP4 from the
            customer-assets CDN. Container, aspect ratio, overlay button,
            and started-state behavior are preserved exactly as before. */}
        <div className="mb-6 fade-up" data-testid="onboarding-video-section">
          <div
            className="relative w-full overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/60 ring-1 ring-amber-500/10 shadow-[0_30px_120px_-60px_rgba(245,158,11,0.4)]"
            style={{ paddingTop: "66.6667%" }}
          >
            <video
              ref={videoRef}
              src={VIDEO_SRC}
              preload="metadata"
              playsInline
              controls
              controlsList="nodownload"
              data-testid="onboarding-video"
              className="absolute inset-0 h-full w-full bg-black object-cover"
            />

            {/* "WATCH WITH SOUND" overlay — shown until the user starts
                the video. Click → unmute + restart + play in-place. */}
            {!started && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <Button
                  type="button"
                  onClick={handleWatchWithSound}
                  data-testid="watch-with-sound-button"
                  aria-label="Play the video with sound"
                  className="pointer-events-auto h-12 rounded-full bg-amber-500 text-[#1c1302] font-bold tracking-[0.18em] uppercase text-xs px-6 hover:bg-amber-400 shadow-[0_18px_50px_-15px_rgba(245,158,11,0.7)] ring-1 ring-amber-300/40"
                >
                  <Volume2 className="h-4 w-4 mr-2" strokeWidth={2.2} />
                  Watch with sound
                </Button>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-center gap-2.5">
            <a
              href="https://platform.openai.com/api-keys"
              target="_blank"
              rel="noreferrer"
              data-testid="get-openai-key-button"
              className="flex-1 sm:flex-none"
            >
              <Button
                type="button"
                className="w-full sm:w-auto h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#082015] font-semibold text-sm px-6 transition-all duration-200 shadow-[0_18px_50px_-15px_rgba(16,185,129,0.55)] ring-1 ring-emerald-300/30"
              >
                <ExternalLink className="h-4 w-4 mr-2" strokeWidth={2} />
                Get Your OpenAI Key
              </Button>
            </a>

            <QuickSetupDialog />
          </div>
        </div>

        <div
          className="relative rounded-2xl border border-white/10 bg-zinc-900/80 backdrop-blur-xl p-6 sm:p-8 gold-glow fade-up"
          data-testid="api-key-gate"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30">
              <KeyRound className="h-4.5 w-4.5" strokeWidth={1.6} />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-white">
                Connect your OpenAI key
              </h2>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              OpenAI API Key
            </label>
            <div className="flex gap-2">
              <Input
                type={show ? "text" : "password"}
                placeholder="sk-..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && save()}
                data-testid="api-key-input"
                className="h-12 bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 font-mono text-sm focus:border-amber-500 focus:ring-amber-500"
              />
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShow((s) => !s)}
                data-testid="toggle-api-key-visibility"
                className="h-12 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800"
              >
                {show ? "Hide" : "Show"}
              </Button>
            </div>
            <Button
              onClick={save}
              data-testid="save-api-key-button"
              className="w-full h-12 rounded-full bg-amber-500 text-[#1c1302] font-semibold hover:bg-amber-400 transition-all duration-200"
            >
              <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.8} />
              Unlock BuddyPass
            </Button>
          </div>

          <div className="mt-6 flex items-center gap-2 text-[11px] text-zinc-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" strokeWidth={1.8} />
            <span>100% secure. Your key lives only in your browser.</span>
          </div>
        </div>

        {/* Optional Advanced AI Automation & Scaling — N8N workflow downloads.
            Placed BELOW the API key card so it doesn't compete with the
            primary onboarding action but is clearly available for power
            users who want to extend BuddyPass into N8N. */}
        <N8nWorkflowsCard />
      </div>
    </div>
  );
};

export default ApiKeyGate;

// ─────────────────────────────────────────────────────────────────────
// Advanced AI Automation & Scaling card
//   - Optional, non-intrusive — sits beneath the API key form.
//   - Single button bulk-downloads all 3 N8N workflow JSON files. Files
//     are served from /n8n/ in the public dir (same-origin, no CORS),
//     so we can use plain anchor downloads with no fetch step.
// ─────────────────────────────────────────────────────────────────────
const N8N_WORKFLOWS = [
  { label: "Asset Builder",       file: "ASSET-BUILDER-AI.json" },
  { label: "Content Intelligence", file: "CONTENT-INTELLIGENCE.json" },
  { label: "Content Production",   file: "CONTENT-PRODUCTION-AI.json" },
];

const N8nWorkflowsCard = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    setDownloading(true);
    // Trigger one download anchor per file, with a tiny stagger so
    // browsers (especially Safari) don't dedupe sequential downloads
    // that land in the same tick. Each anchor uses the `download`
    // attribute so the browser writes the file rather than navigating.
    N8N_WORKFLOWS.forEach((wf, i) => {
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = `${process.env.PUBLIC_URL || ""}/n8n/${wf.file}`;
        a.download = wf.file;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }, i * 250);
    });
    // Reset the button state shortly after the last anchor fires.
    setTimeout(() => {
      setDownloading(false);
      toast.success("3 N8N workflows downloaded — import them into N8N.");
    }, N8N_WORKFLOWS.length * 250 + 200);
  };

  return (
    <div
      data-testid="n8n-workflows-card"
      className="mt-6 relative group rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900/90 via-zinc-950/90 to-zinc-900/70 p-5 sm:p-6 overflow-hidden transition-all duration-300 hover:border-violet-500/40 hover:shadow-[0_30px_80px_-30px_rgba(139,92,246,0.35)]"
    >
      {/* Subtle ambient grid + glow — quiet "AI automation" aesthetic */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07] group-hover:opacity-[0.1] transition-opacity duration-500"
        style={{
          backgroundImage:
            "linear-gradient(rgba(139,92,246,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.6) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage:
            "radial-gradient(ellipse at top right, black 0%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at top right, black 0%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-violet-500/15 blur-3xl"
      />

      <div className="relative flex items-start gap-3.5 mb-3.5">
        <div className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-500/15 text-violet-300 ring-1 ring-violet-500/40 shadow-[0_8px_24px_-10px_rgba(139,92,246,0.5)]">
          <Workflow className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div className="space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-violet-300">
            Optional · Power user
          </div>
          <h3 className="font-display text-lg sm:text-xl font-black tracking-tight text-white leading-tight">
            Advanced AI Automation & Scaling
          </h3>
        </div>
      </div>

      <p className="relative text-[13.5px] sm:text-sm leading-relaxed text-zinc-300 mb-5">
        Fully built <strong className="text-white font-semibold">N8N workflows</strong> to create and publish premium ebooks{" "}
        <span className="text-violet-300 font-semibold">(Asset Builder)</span> and viral AI videos with voiceovers, scripts &amp; trending YouTube clips automatically{" "}
        <span className="text-violet-300 font-semibold">(Content Intelligence + Content Production)</span>. Download and import directly into N8N.
      </p>

      <div className="relative flex flex-col sm:flex-row sm:items-center gap-3">
        <Button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          data-testid="download-n8n-workflows-button"
          className="w-full sm:w-auto h-12 rounded-full bg-gradient-to-r from-violet-500 via-violet-500 to-fuchsia-500 hover:from-violet-400 hover:via-violet-400 hover:to-fuchsia-400 text-white font-bold text-[13px] tracking-wider uppercase px-6 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_20px_50px_-15px_rgba(139,92,246,0.75)] ring-1 ring-white/10 disabled:opacity-70 disabled:cursor-wait"
        >
          <Download
            className={`h-4 w-4 mr-2.5 ${downloading ? "animate-bounce" : ""}`}
            strokeWidth={2.4}
          />
          {downloading ? "Downloading…" : "Download N8N Workflows"}
        </Button>

        <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-violet-400" />
          3 ready-to-import .json files
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Quick Setup dialog — visual step-by-step for first-time users
// who don't yet have an OpenAI API key. Dialog is keyboard-accessible
// (built on Radix Dialog under the shadcn/ui wrapper).
// ─────────────────────────────────────────────────────────────────────
const QUICK_SETUP_STEPS = [
  {
    title: "Open the OpenAI platform",
    body: (
      <>
        Go to{" "}
        <a
          href="https://platform.openai.com/"
          target="_blank"
          rel="noreferrer"
          className="text-amber-300 underline decoration-amber-500/40 underline-offset-2 hover:text-amber-200"
        >
          platform.openai.com
        </a>
      </>
    ),
  },
  { title: "Sign in", body: "Sign in or create your OpenAI account." },
  {
    title: "Add at least $5 in API credits",
    body: (
      <>
        Settings → Billing → Add Payment Method.
        <span className="mt-1.5 block rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12.5px] text-amber-200">
          Recommended: start with <strong>$5</strong> in credits to test BuddyPass AI.
        </span>
      </>
    ),
  },
  { title: "Open your profile menu", body: "Click your profile icon in the top-right corner." },
  { title: "Go to API Keys", body: "Open “View API Keys”." },
  { title: "Create a new key", body: "Click “Create new secret key”." },
  { title: "Name your key", body: "Name it something memorable — e.g. “BuddyPass AI”." },
  {
    title: "Copy + paste into BuddyPass AI",
    body: "Copy the full key immediately and paste it into the field above.",
  },
];

const QuickSetupDialog = () => (
  <Dialog>
    <DialogTrigger asChild>
      <Button
        type="button"
        data-testid="quick-setup-button"
        aria-label="Open the OpenAI Quick Setup guide"
        className="h-12 rounded-full bg-zinc-900 hover:bg-zinc-800 border border-emerald-500/40 hover:border-emerald-500/70 text-emerald-200 hover:text-emerald-100 font-semibold text-sm px-4 transition-all"
      >
        <ListChecks className="h-4 w-4 mr-1.5" strokeWidth={2} />
        Quick Setup
      </Button>
    </DialogTrigger>

    <DialogContent
      data-testid="quick-setup-dialog"
      className="bg-zinc-950 border border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto"
    >
      <DialogHeader>
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30">
          <ListChecks className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <DialogTitle className="font-display text-2xl font-black tracking-tight text-white mt-3">
          Get your <span className="text-emerald-300">OpenAI key</span> in 8 steps
        </DialogTitle>
        <DialogDescription className="text-zinc-400 text-sm leading-relaxed">
          BuddyPass AI uses your own OpenAI key — your key, your cost, your control. Total setup time: ~3 minutes.
        </DialogDescription>
      </DialogHeader>

      <ol className="mt-2 space-y-3">
        {QUICK_SETUP_STEPS.map((s, i) => (
          <li
            key={i}
            data-testid={`quick-setup-step-${i + 1}`}
            className="rounded-xl border border-white/10 bg-zinc-900/70 p-4 flex gap-3.5"
          >
            <div className="shrink-0 mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30 text-[12px] font-black">
              {i + 1}
            </div>
            <div className="space-y-0.5">
              <div className="font-semibold text-white text-[14px] leading-snug">{s.title}</div>
              <div className="text-zinc-300 text-[13.5px] leading-relaxed">{s.body}</div>
            </div>
          </li>
        ))}
      </ol>

      <div
        data-testid="quick-setup-warning"
        className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3.5"
      >
        <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-400 shrink-0" strokeWidth={2} />
        <div className="text-[12.5px] text-amber-200 leading-relaxed">
          <strong>Important:</strong> OpenAI only shows the secret key <em>once</em> — save it somewhere secure (a password manager) before closing the page.
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 text-[12px] text-emerald-300">
        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
        Your API key is now ready to connect to BuddyPass AI.
      </div>

      <div className="mt-5 flex justify-end">
        <a
          href="https://platform.openai.com/api-keys"
          target="_blank"
          rel="noreferrer"
          data-testid="quick-setup-open-platform"
        >
          <Button
            type="button"
            className="h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#082015] font-semibold text-sm px-5"
          >
            <ExternalLink className="h-4 w-4 mr-1.5" strokeWidth={2} />
            Open OpenAI Platform
          </Button>
        </a>
      </div>
    </DialogContent>
  </Dialog>
);
